import * as _ from 'lodash';
import { FormGroup } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import {
  DEPENDENCY_SIGNATURE_CHANGE_TOO_LARGE_ERROR,
  DEPENDENCY_SIGNATURE_TOO_LONG_ERROR,
  splitDependencySignatures,
  TOO_MANY_DEPENDENCY_SIGNATURE_CHANGES_ERROR,
  TOO_MANY_DEPENDENCY_SIGNATURES_ERROR,
} from '../../../../../shared/organizations/dependencySignatures';
import { isFreeTier } from '../../../../../shared/subscriptions/utils';
import { window } from '../../../../globals';
import { ErrorAlert } from '../../../generic/ErrorAlert';
import LabeledTextInput from '../../../generic/Forms/LabeledTextInput';
import PaidFeatureGate from '../../../generic/Gates/PaidFeatureGate';
import Text from '../../../generic/Text';
import type { GeneralSettings } from '../../../../../shared/settings/general';

import PackageLabelSelector from './PackageLabelsSelector/PackageLabelSelector';

type Props = {
  settings: GeneralSettings;
  onUpdateSettings: (settings: Partial<GeneralSettings>) => void;
  isDisabled: boolean;
  errors: string[] | null;
};

const SIGNATURE_BOUND_ERRORS = [
  TOO_MANY_DEPENDENCY_SIGNATURES_ERROR,
  TOO_MANY_DEPENDENCY_SIGNATURE_CHANGES_ERROR,
  DEPENDENCY_SIGNATURE_TOO_LONG_ERROR,
  DEPENDENCY_SIGNATURE_CHANGE_TOO_LARGE_ERROR,
];

/**
 * Displays the components section of the general settings page.
 */
const ComponentsSection: VoidFunctionComponent<Props> = ({ settings, isDisabled, onUpdateSettings, errors }) => {
  const signatureErrors = errors?.filter((error) => SIGNATURE_BOUND_ERRORS.some((bound) => error.includes(bound)))
    ?? [];
  const signatures = _.uniq(splitDependencySignatures(settings.dependencySignatures));
  return (
    <>
      <hr />
      <Text.H3>Packages</Text.H3>
      <PaidFeatureGate
        isRestricted={isFreeTier({
          plan: window.user?.organization?.stripe_plan,
          stripePriceLimit: window.user?.organization?.stripePriceLimit,
        })}
      >
        <PackageLabelSelector
          settings={settings}
          onUpdateSettings={onUpdateSettings}
          isDisabled={isDisabled}
        />
      </PaidFeatureGate>
      <div>
        {signatureErrors.length > 0 && <ErrorAlert status={{ errors: signatureErrors }} />}
        <FormGroup>
          <LabeledTextInput
            labelVariant="legacy"
            label="Internal Package Signatures"
            value={settings.dependencySignatures || ''}
            placeholder="Separate multiple signatures with commas"
            onChange={(dependencySignatures) => {
              onUpdateSettings({
                dependencySignatures,
              });
            }}
            isDisabled={isDisabled}
          />
        </FormGroup>
        {signatures.length > 0 ? (
          <p>
            <span>FOSSA will ignore dependencies that match:</span>
            {signatures.map((dependencySignature) => (
              <code key={dependencySignature} className="margin-right-sm">{dependencySignature}</code>
            ))}
          </p>
        ) : (
          <p>
            Use this field to specify signatures for marking internal package naming conventions. FOSSA will recognize
            any package that contains these signatures in their names as internal dependencies and will ignore these
            dependencies.
          </p>
        )}
      </div>
    </>
  );
};

export default ComponentsSection;
